import React, { useState } from "react";
import { Form, SecondaryButton, NegativeButton } from "common/Forms";
import { InRow, ResponsiveGap } from "common/Grid";
import { TableRenderer } from "common/Table";
import { useNavigate } from "react-router-dom";
import { BiTrash } from "react-icons/bi";
import { ModalDialog } from "common/Dialog";
import { Grid, GridCell } from "common/Grid/Grid";
import { useDeleteSalesUserMutation } from "store/action/salesUserApi";
import { toast } from "react-toastify";
import { LuPencilLine } from "react-icons/lu";
import { PositiveStatusLabel, NegativeStatusLabel } from "common/Label";
import sortAsc from "assets/img/sort-asc.svg";
import sortDesc from "assets/img/sort-desc.svg";

const SortHeader = ({ label, sortByValue, sorting, onSortChange }) => {
  const isAscActive = sorting?.sortBy === sortByValue && sorting?.sortOrder === 1;
  const isDescActive = sorting?.sortBy === sortByValue && sorting?.sortOrder === 2;
  return (
    <div className="flex items-center justify-between w-full">
      <span>{label}</span>
      <div className="flex items-center gap-1">
        <img
          src={sortAsc}
          alt="sort asc"
          onClick={() => onSortChange?.(sortByValue, 1)}
          className={`cursor-pointer transition-opacity hover:opacity-100 ${isAscActive ? "opacity-100" : "opacity-35"}`}
          style={{ width: 20, height: 20 }}
        />
        <img
          src={sortDesc}
          alt="sort desc"
          onClick={() => onSortChange?.(sortByValue, 2)}
          className={`cursor-pointer transition-opacity hover:opacity-100 ${isDescActive ? "opacity-100" : "opacity-35"}`}
          style={{ width: 20, height: 20 }}
        />
      </div>
    </div>
  );
};

export const TableView = ({ salesUserList, sorting, onSortChange }) => {
  const navigate = useNavigate();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteSalesUser, { isLoading: isDeleting }] = useDeleteSalesUserMutation();

  const onDeleteConfirm = async () => {
    if (!selectedUser || isDeleting) {
      return;
    }

    const payload = {
      salesUserId: selectedUser.id || selectedUser._id || selectedUser.salesUserId,
    };

    try {
      const res = await deleteSalesUser(payload);

      if (res?.data?.status === "SUCCESS") {
        toast.success(res?.data?.message || "User deleted successfully");
        setDeleteModalOpen(false);
        setSelectedUser(null);
      } else {
        toast.error(res?.error?.data?.message || "Something went wrong, please try again.");
        setDeleteModalOpen(false);
      }
    } catch (error) {
      toast.error("Something went wrong, please try again.");
      setDeleteModalOpen(false);
    }
  };

  const onDeleteClick = (user) => {
    if (isDeleting) {
      return;
    }

    setSelectedUser(user);
    setDeleteModalOpen(true);
  };

  return (
    <Form>
      <TableRenderer
        data={salesUserList ?? []}
        columns={[
          {
            name: "Name",
            widthRequest: { min: 220 },
            headerRenderer: () => <SortHeader label="Name" sortByValue={1} sorting={sorting} onSortChange={onSortChange} />,
            bodyRenderer: x => x?.name ? x.name.charAt(0).toUpperCase() + x.name.slice(1) : <div>-</div>
          },
          {
            name: "Email",
            widthRequest: { min: 220 },
            headerRenderer: () => <SortHeader label="Email" sortByValue={2} sorting={sorting} onSortChange={onSortChange} />,
            bodyRenderer: (x) => x?.email || <div>-</div>,
          },
          {
            name: "Phone",
            widthRequest: { min: 150 },
            bodyRenderer: (x) => x?.phone || <div>-</div>,
          },
          {
            name: "Role",
            widthRequest: { min: 150 },
            bodyRenderer: (x) => x?.role || <div>-</div>,
          },
          {
            name: "Status",
            widthRequest: { min: 120 },
            bodyRenderer: (x) =>
              x?.isActive ? (
                <PositiveStatusLabel title="Active" />
              ) : (
                <NegativeStatusLabel title="Inactive" />
              ),
          },
          {
            name: "Actions",
            widthRequest: { min: 150 },
            classNames: ["!text-center"],
            headerRenderer: (col) => <div className="w-full text-center">{col.name}</div>,
            bodyRenderer: (x) => (
              <InRow gap={1} classNames={["justify-center"]}>
                <SecondaryButton
                  disabled={!x?.isActive}
                  action={() => navigate(`/edit-sales-user`, { state: { user: x } })}
                  title="Edit User">
                  <LuPencilLine size={20} />
                </SecondaryButton>
                <NegativeButton
                  disabled={!x?.isActive}
                  action={() => onDeleteClick(x)}
                  title="Delete User">
                  <BiTrash size={20} />
                </NegativeButton>
              </InRow>
            ),
          },
        ]}
      />

      <ModalDialog isOpened={deleteModalOpen} title="Confirm Delete">
        <div className="p-5 text-center">Are you sure you want to delete this user?</div>
        <ResponsiveGap left={false} right={false}>
          <Grid columns={2}>
            <GridCell>
              <NegativeButton isWide={true} action={onDeleteConfirm} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Delete"}
              </NegativeButton>
            </GridCell>
            <GridCell>
              <SecondaryButton
                disabled={isDeleting}
                action={() => {
                  setDeleteModalOpen(false);
                  setSelectedUser(null);
                }}
                isWide={true}>
                Cancel
              </SecondaryButton>
            </GridCell>
          </Grid>
        </ResponsiveGap>
      </ModalDialog>
    </Form>
  );
};
